/** @jsx jsx */
import { jsx, Spinner, Text } from "theme-ui";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useFirestore } from "react-redux-firebase";
import {
  ActionButton,
  IconButton,
  Stack,
  StackItem,
  DefaultButton,
} from "office-ui-fabric-react";
import { doFormatShortDate } from "@heartfulnessinstitute/react-hfn-forms/dist/formatting";
import useAuth from "gatsby-plugin-hfn-profile/auth/useAuth";
import ConfirmButton from "gatsby-plugin-hfn-profile/components/ConfirmButton";

import { RenderTableData } from "@heartfulnessinstitute/react-hfn-forms/dist/ui/RenderTable";

import Modal from "gatsby-plugin-hfn-profile/components/Modal";
import EditingRecord from "./EditingRecord";
import config, { colors } from "./config";
import { aimsMandatoryFlds } from "./fields";

const { cachingKey, collection, initialQuery } = config;

const dateRange = (a, b) => {
  const a1 = a ? doFormatShortDate(a) : "";
  const b1 = b ? doFormatShortDate(b) : "";
  const currentYear = `, ${new Date().getFullYear()}`;

  const a2 = a1.replace(currentYear, "");
  const b2 = b1.replace(currentYear, "");

  return `${a2} - ${b2}`;
};
const cond = (c, val) => (c ? [val] : []);

function MyRecords({ adminConfig }) {
  const oppy = adminConfig?.id;
  const oppyKey = cachingKey + oppy;
  const { srcmProfile = {} } = useAuth();

  const { userFirebaseUid: uid } = srcmProfile;

  const [people, setPeople] = useState(null);
  const [loader, setLoader] = useState(0);
  const [errors, setErrors] = useState("");

  const isError = Object.values(errors).filter((r) => r).length > 0;

  const firestore = useFirestore();
  const [limit, setLimit] = useState(10);
  const dbRecords = useSelector((state) =>
    oppyKey in state.firestore.ordered
      ? state.firestore.ordered[oppyKey]
      : undefined
  );
  const showMore = dbRecords ? dbRecords.length === limit : false;
  const incr10 = useCallback(() => setLimit((a) => a + 10), []);

  useEffect(() => {
    const listenerSettings = {
      ...initialQuery({ uid, oppy }),
      storeAs: oppyKey,
      limit,
    };
    firestore.setListener(listenerSettings);
    return function cleanup() {
      firestore.unsetListener(listenerSettings);
    };
  }, [firestore, uid, limit, oppyKey, oppy]);

  const onDelete = useCallback(
    (r) => {
      // console.log(r);
      firestore.delete(`${collection}/${r.id}`);
    },
    [firestore]
  );
  const onUpdate = useCallback(() => {
    const flds = [
      ...aimsMandatoryFlds,
      ...adminConfig.aims_flds,
      ...adminConfig.visit_flds,
    ];
    Object.entries(people).forEach(([id, record]) => {
      setLoader((x) => x + 1);
      firestore
        .set(
          `${collection}/${id}`,
          Object.fromEntries(flds.map((f) => [f, record[f] || null])),
          { merge: true }
        )
        .then(() => {
          setLoader((x) => x - 1);

          setPeople(null);
        })
        .catch((e) => {
          setLoader((x) => x - 1);

          setErrors({ [id]: e.toString() });
        });
    });
  }, [firestore, setErrors, setPeople, people, adminConfig]);

  const closeEdit = useCallback(() => {
    setPeople(null);
    setErrors({});
  }, [setErrors, setPeople]);

  const colDefns = useMemo(
    () => [
      ["Name", (r) => <div>{r.name}</div>],
      ...cond(
        adminConfig?.visit_flds?.includes("arrival") ||
          adminConfig?.visit_flds?.includes("departure"),
        ["Visit Dates", (r) => dateRange(r.arrival, r.departure)]
      ),
      ...cond(adminConfig?.visit_flds?.includes("vlntr_area"), [
        "Volunteer Areas",
        (r) => (
          <div>{r.vlntr_area && r.vlntr_area.map((a) => a).join(", ")}</div>
        ),
      ]),
      ...cond(
        adminConfig?.visit_flds?.includes("stay"),

        ["Stay Preference", (r) => r.stay]
      ),
      [
        "",
        (r) => (
          <Stack horizontal>
            <IconButton
              onClick={() => setPeople({ [r.id]: r })}
              iconProps={{ iconName: adminConfig?.active ? "Edit" : "View" }}
            />
            {adminConfig?.active && (
              <StackItem>
                <ConfirmButton
                  title={`Deleting ${r.name}`}
                  onClick={() => onDelete(r)}
                >
                  <IconButton iconProps={{ iconName: "Trash" }} />
                </ConfirmButton>
              </StackItem>
            )}
          </Stack>
        ),
      ],
    ],
    [setPeople, onDelete, adminConfig]
  );
  return (
    <div sx={{ mb: 3 }}>
      {people && (
        <Modal style_main={{ backgroundColor: colors.shade6 }}>
          <EditingRecord
            people={people}
            id={Object.keys(people)[0]}
            setPeople={setPeople}
            errors={errors}
            setErrors={setErrors}
            adminConfig={adminConfig}
          />
          <Stack horizontal horizontalAlign="space-between" sx={{ p: 3 }}>
            {adminConfig?.active && (
              <DefaultButton
                primary
                disabled={isError || loader}
                onClick={onUpdate}
              >
                Update
              </DefaultButton>
            )}
            {!!loader && <Spinner />}
            <DefaultButton
              onClick={closeEdit}
              text={adminConfig?.active ? "Discard" : "Close"}
            />
          </Stack>
        </Modal>
      )}
      {dbRecords === undefined && <Spinner />}
      {dbRecords !== undefined && (
        <div>
          {dbRecords.length === 0 && (
            <div sx={{ textAlign: "center", m: 2 }}>
              <Text variant="description"> No Records </Text>
            </div>
          )}
          {dbRecords.length > 0 && (
            <div sx={{ fontSize: "0.8em" }}>
              <RenderTableData
                showHeader
                colDefns={colDefns}
                rowData={dbRecords}
              />

              {showMore && (
                <ActionButton text="show more entries ..." onClick={incr10} />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
MyRecords.propTypes = {
  adminConfig: PropTypes.shape({
    id: PropTypes.string,
    active: PropTypes.bool,
    aims_flds: PropTypes.arrayOf(PropTypes.shape({})),
    visit_flds: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
};

export default MyRecords;
